/**
 * HelpDrawer - Hilfe-Drawer mit Dokumentation
 *
 * Zeigt eine durchsuchbare Liste von Dokumentationen in einem Drawer an.
 */

import { useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import {
  HelpCircle,
  ChevronLeft,
  FileText,
  BookOpen,
  Code,
  ExternalLink,
} from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { listDocs, getDoc, type DocInfo } from "@/services/docs"

// Kategorie-Icons
const categoryIcons: Record<string, React.ElementType> = {
  integrations: ExternalLink,
  development: Code,
  projektbase: BookOpen,
  default: FileText,
}

// Kategorie-Labels
const categoryLabels: Record<string, string> = {
  integrations: "Integrationen",
  development: "Entwicklung",
  projektbase: "Projektbase",
}

export function HelpDrawer() {
  const [open, setOpen] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null)

  // Dokumentations-Liste laden
  const {
    data: docsList,
    isLoading: isLoadingList,
  } = useQuery({
    queryKey: ["docs-list"],
    queryFn: listDocs,
    enabled: open,
    staleTime: 5 * 60 * 1000, // 5 Minuten cache
  })

  // Einzelne Dokumentation laden
  const {
    data: docContent,
    isLoading: isLoadingContent,
  } = useQuery({
    queryKey: ["doc", selectedDoc],
    queryFn: () => getDoc(selectedDoc!),
    enabled: !!selectedDoc,
    staleTime: 5 * 60 * 1000,
  })

  // Reset bei Schließen
  useEffect(() => {
    if (!open) {
      // Verzögertes Reset für Animation
      const timer = setTimeout(() => setSelectedDoc(null), 300)
      return () => clearTimeout(timer)
    }
  }, [open])

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" title="Hilfe & Dokumentation">
          <HelpCircle className="h-5 w-5" />
          <span className="sr-only">Hilfe</span>
        </Button>
      </SheetTrigger>

      <SheetContent className="w-full sm:max-w-xl lg:max-w-2xl">
        <SheetHeader className="space-y-1">
          {selectedDoc && docContent ? (
            <>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setSelectedDoc(null)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <SheetTitle className="text-lg">
                  {docContent.title}
                </SheetTitle>
              </div>
              <SheetDescription>{docContent.description}</SheetDescription>
            </>
          ) : (
            <>
              <SheetTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Hilfe & Dokumentation
              </SheetTitle>
              <SheetDescription>
                Anleitungen und technische Dokumentation für NetzERP
              </SheetDescription>
            </>
          )}
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-8rem)] mt-6 pr-4">
          {selectedDoc ? (
            // Dokumentations-Inhalt
            <DocumentContent
              content={docContent?.content}
              isLoading={isLoadingContent}
            />
          ) : (
            // Dokumentations-Liste
            <DocumentList
              categories={docsList?.categories}
              isLoading={isLoadingList}
              onSelect={setSelectedDoc}
            />
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}

// Dokumentations-Liste Komponente
interface DocumentListProps {
  categories?: Record<string, DocInfo[]>
  isLoading: boolean
  onSelect: (docId: string) => void
}

function DocumentList({ categories, isLoading, onSelect }: DocumentListProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ))}
      </div>
    )
  }

  if (!categories || Object.keys(categories).length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>Keine Dokumentation verfügbar</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {Object.entries(categories).map(([category, docs]) => {
        const Icon = categoryIcons[category] || categoryIcons.default
        const label = categoryLabels[category] || category

        return (
          <div key={category}>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
              <Icon className="h-4 w-4" />
              {label}
            </h3>
            <div className="space-y-2">
              {docs.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => onSelect(doc.id)}
                  disabled={!doc.available}
                  className="w-full text-left p-3 rounded-lg border bg-card hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="font-medium">{doc.title}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {doc.description}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Dokumentations-Inhalt Komponente
interface DocumentContentProps {
  content?: string
  isLoading: boolean
}

function DocumentContent({ content, isLoading }: DocumentContentProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    )
  }

  if (!content) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Dokumentation konnte nicht geladen werden</p>
      </div>
    )
  }

  return (
    <article className="prose prose-sm dark:prose-invert max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Code-Blöcke mit Styling
          code({ className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "")
            const isInline = !match

            if (isInline) {
              return (
                <code
                  className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono"
                  {...props}
                >
                  {children}
                </code>
              )
            }

            return (
              <pre className="bg-muted p-4 rounded-lg overflow-x-auto">
                <code className={`language-${match[1]} text-sm font-mono`} {...props}>
                  {children}
                </code>
              </pre>
            )
          },
          // Tabellen mit Styling
          table({ children }) {
            return (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse border border-border">
                  {children}
                </table>
              </div>
            )
          },
          th({ children }) {
            return (
              <th className="border border-border bg-muted px-3 py-2 text-left font-semibold">
                {children}
              </th>
            )
          },
          td({ children }) {
            return (
              <td className="border border-border px-3 py-2">{children}</td>
            )
          },
          // Links extern öffnen
          a({ href, children }) {
            const isExternal = href?.startsWith("http")
            return (
              <a
                href={href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                className="text-primary hover:underline"
              >
                {children}
                {isExternal && (
                  <ExternalLink className="inline-block h-3 w-3 ml-1" />
                )}
              </a>
            )
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </article>
  )
}
